import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { RoleConst } from '@blcloud/bl-datamodel/const/role';
import { BlAuthGuard, BlErrorGuard, PwAuthnGuard } from '@blcloud/bl-ng-common';
import {
  ErrorComponent,
  ErrorDeactivateGuard,
  ServiceCondition,
  ServiceGuard,
  PermissionGuard,
  ContractGuard,
  DeviceSessionGuard,
  PageId
} from '@blcloud/bl-ng-share-module';
import { AutoLoginGuard } from './core/guard/auto-login/auto-login.guard';
import { LoginPageComponent } from './page/login-page/login-page.component';
import { RedirectPageComponent } from './page/redirect-page/redirect-page.component';

/** ルーティング */
const routes: Routes = [
  { path: 'error', component: ErrorComponent, canActivate: [BlErrorGuard], canDeactivate: [ErrorDeactivateGuard] },  // 共通エラー画面

  // 業務メニュー
  {
    path: 'biz',
    loadChildren: './page/biz-menu-page/biz-menu-page.module#BizMenuPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard]
  },
  // 基本情報設定ウィザード
  {
    path: 'wizard',
    loadChildren: './page/wizard-page/wizard-page.module#WizardPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, PermissionGuard],
    data: { blFunctionId: 'BM100-121900-E' }
  },
  // 伝票検索
  {
    path: 'salesslip/search',
    loadChildren: './page/sales-slip-search-page/sales-slip-search-page.module#SalesSlipSearchPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  // 伝票履歴
  {
    path: 'salesslip/history',
    loadChildren: './page/sales-slip-history-page/sales-slip-history-page.module#SalesSlipHistoryPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  // 伝票履歴検索
  {
    path: 'salesslip/history/search',
    loadChildren: './page/sales-slip-history-search-page/sales-slip-history-search-page.module#SalesSlipHistorySearchPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  // 受付
  {
    path: 'reception/entry',
    loadChildren: './page/recept-entry-page/recept-entry-page.module#ReceptEntryPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  // 顧客照会
  {
    path: 'customerinquiry',
    loadChildren: 'app/page/customer-inquiry-page/customer-inquiry-page.module#CustomerInquiryPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  // 顧客入力画面
  {
    path: 'customer/entry',
    loadChildren: 'app/page/customer-entry-page/customer-entry-page.module#CustomerEntryPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard, DeviceSessionGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_CUSTOMER_MGT,
      pageId: PageId.CustomerEntry
    }
  },
  // 車両入力画面
  {
    path: 'vehicle/entry',
    loadChildren: 'app/page/vehicle-entry-page/vehicle-entry-page.module#VehicleEntryPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard, DeviceSessionGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_VEHICLE_MGT,
      pageId: PageId.VehicleEntry
    }
  },
  // 顧客車両検索画面
  {
    path: 'vehicle/search',
    // AVIS.C start
    loadChildren: 'app/page/ab-customer-vehicle-search-page/ab-customer-vehicle-search-page.module#AbCustomerVehicleSearchPageModule',
    // AVIS.C end
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: [ContractConst.SERVICEID_OPT_CMN_CUSTOMER_MGT, ContractConst.SERVICEID_OPT_CMN_VEHICLE_MGT],
      condition: ServiceCondition.And
    }
  },
  { path: 'contact', pathMatch: 'full', redirectTo: 'contact/callList' },
  // コンタクト一覧
  {
    path: 'contact/contactList',
    loadChildren: 'app/page/contact-list-page/contact-list-page.module#ContactListPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  // コンタクトカレンダー
  {
    path: 'contact/contactCalendar',
    loadChildren: 'app/page/contact-calendar-page/contact-calendar-page.module#ContactCalendarPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  // 架電一覧
  {
    path: 'contact/callList',
    loadChildren: 'app/page/call-list-page/call-list-page.module#CallListPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_CONTACT_SCHEDULE_MAIN
    }
  },
  // プロフィール登録一覧
  {
    path: 'profile/entries',
    loadChildren: './page/profile-entries-page/profile-entries-page.module#ProfileEntriesPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_PROFILE_ENTRY,
    }
  },
  // 一括編集
  {
    path: 'bulkedit',
    loadChildren: 'app/page/bulk-edit-page/bulk-edit-page.module#BulkEditPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  // フォロワー(LINE登録)一覧
  {
    path: 'follower/search',
    loadChildren: 'app/page/follower-search-page/follower-search-page.module#FollowerSearchPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard, PermissionGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_LINE_BASE,
      blFunctionId: RoleConst.ROLEID_FUNC_ALL_LINE_FOLLOW_LIST
    }
  },

  // LINEお友達登録QR
  {
    path: 'line-qr',
    loadChildren: 'app/page/line-qr-page/line-qr-page.module#LineQrPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },

  // くるまコンシェルジュユーザー連携QR
  {
    path: 'car-concierge-qr',
    loadChildren: 'app/page/car-concierge-qr-page/car-concierge-qr-page.module#CarConciergeQrPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_CASE_CARCONCIERGE_LINK,
    }
  },

  // アンケート回答QR
  {
    path: 'survey-qr',
    loadChildren: 'app/page/survey-qr-page/survey-qr-page.module#SurveyQrPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_SURVEY_BASE,
    }
  },

  // ローン審査一覧画面
  {
    path: 'loan/search',
    loadChildren: 'app/page/loan-application-search-page/loan-application-search-page.module#LoanApplicationSearchPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard]
  },

  // SPA振り分けリダイレクトページ：通知トリガー
  {
    path: 'notice/redirect',
    loadChildren: 'app/page/notice-redirect-page/notice-redirect-page.module#NoticeRedirectPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard]
  },
  // 帳票出力状況
  {
    path: 'report-output-status',
    loadChildren: 'app/page/report-output-status-page/report-output-status-page.module#ReportOutputStatusPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard]
  },
  // メンテナンスパック契約検索
  {
    path: 'maintenancepack/contract-search',
    loadChildren: 'app/page/mainte-pack-contract-search-page/mainte-pack-contract-search-page.module#MaintePackContractSearchPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_MAC_MAINTE_PACK_BASE,
    }
  },
  // メンテナンスパック預り金入力
  {
    path: 'maintenancepack/deposit-entry',
    loadChildren: 'app/page/mainte-pack-deposit-entry-page/mainte-pack-deposit-entry-page.module#MaintePackDepositEntryPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard]
  },
  // CTI連携リダイレクトページ
  {
    path: 'cti/redirect',
    loadChildren: 'app/page/cti-redirect-page/cti-redirect-page.module#CtiRedirectPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard]
  },

  // 受付一覧
  {
    path: 'recept/list',
    loadChildren: 'app/page/recept-list-page/recept-list-page.module#ReceptListPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard]
  },

  // クイック検索
  {
    path: 'quick/search',
    loadChildren: 'app/page/quick-search-page/quick-search-page.module#QuickSearchPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard, ServiceGuard, PermissionGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_QUICK_SEARCH,
      blFunctionId: RoleConst.ROLEID_FUNC_ALL_QUICK_SEARCH
    }
  },
  // ホリデー車検整備保証書履歴
  {
    path: 'hdvcleinspmaintewarranty/history',
    loadChildren: './page/hd-vcle-insp-page/hd-vcle-insp-mainte-warranty-history-page.module#HdVcleInspWarrantyHistoryPageModule',
    canActivate: [PwAuthnGuard, BlAuthGuard, ContractGuard],
  },
  {
    path: 'login',
    component: LoginPageComponent,
    canActivate: [AutoLoginGuard],
    data: {
      redirectPath: 'biz'
    }
  },
  { path: 'redirect', component: RedirectPageComponent },
  // Default
  { path: '', pathMatch: 'full', redirectTo: 'biz' },
  { path: '**', redirectTo: 'biz' }
];

/**
 * アプリケーションルーティングモジュール
 */
@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}



// WEBPACK FOOTER //
// ./src/app/app-routing.module.ts